import appSettings from '../appsettings.json';
import { SessionContext } from "@/context/SessionContext";
import { Song } from "@/javascript/library-objects";
import {
	ActionIcon,
	Badge,
	Button,
	Card,
	Group,
	Popover,
	Text
} from "@mantine/core";
import { IconHeart, IconHeartFilled } from "@tabler/icons-react";
import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CachedImage from "./misc/CachedImage";
import SongPreview from "./SongPreview";

type SongBlockProps = {
	song: Song,
	type: string // "library" or "marketplace"
};

const SongBlock: React.FC<SongBlockProps> = ({ song, type }) => {
	const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
	const [songInLibrary, setSongInLibrary] = useState<boolean>(false);
	const [songInCart, setSongInCart] = useState<boolean>(false);
	const [songInWishlist, setSongInWishlist] = useState<boolean>(false);
	const [popoverOpened, setPopoverOpened] = useState(false);

	const session = useContext(SessionContext);
	if (session === undefined) {
		throw new Error("SongBlock must be a child of SessionContext.");
	}

	const navigate = useNavigate();

	const hasExtraTags = song.tags && song.tags.length > 3;
	const visibleTags = hasExtraTags ? song.tags?.slice(0, 3) : song.tags ?? [];
	const extraTags = hasExtraTags ? song.tags?.slice(3) ?? [] : [];

	const tagBadgesToRender = (visibleTags ?? []).map((tag) => (
		<Badge
			variant="light"
			size="sm"
			key={tag}
			style={{ display: "flex" }}
		>
			{tag}
		</Badge>
	));

	if (hasExtraTags) {
		tagBadgesToRender.push(
			<Popover
				width={200}
				position="bottom"
				withArrow
				shadow="md"
				opened={popoverOpened}
				onClose={() => setPopoverOpened(false)}
				key="extra"
			>
				<Popover.Target>
					<Badge
						variant="light"
						style={{ cursor: "pointer", display: "flex" }}
						onMouseEnter={() => setPopoverOpened(true)}
						onMouseLeave={() => setPopoverOpened(false)}
					>
						...
					</Badge>
				</Popover.Target>
				<Popover.Dropdown
					onMouseEnter={() => setPopoverOpened(true)}
					onMouseLeave={() => setPopoverOpened(false)}
				>
					<Group gap={7} mt={5}>
						{extraTags.map((tag) => (
							<Badge
								variant="light"
								style={{ display: "flex" }}
								key={tag}
							>
								{tag}
							</Badge>
						))}
					</Group>
				</Popover.Dropdown>
			</Popover>
		);
	}

	const artists = song.artists.map((artist) => (
		<Text key={artist.artistId} fz="md" mt="xs">
			{artist.artistName}
		</Text>
	));

	useEffect(() => {
		setSongInLibrary((session.userSongs ?? []).map(userSong => userSong?.songId).includes(song.songId));
	}, [session.userSongs, song.songId]);

	useEffect(() => {
		setSongInCart((session.songsInCart ?? []).map(cartSong => cartSong?.songId).includes(song.songId));
	}, [session.songsInCart, song.songId]);

	useEffect(() => {
		setSongInWishlist((session.songsInWishlist ?? []).map(wishlistSong => wishlistSong?.songId).includes(song.songId));
	}, [session.songsInWishlist, song.songId]);

	const handleAddToCart = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		if (songInCart) {
			session.setSongsInCart((session.songsInCart ?? []).filter(cartSong => cartSong.songId !== song.songId));
		} else {
			session.setSongsInCart((session.songsInCart ?? []).concat(song));
		}
	}, [session.songsInCart, songInCart, song]);

	const handleAddToWishlist = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		if (songInWishlist) {
			session.setSongsInWishlist((session.songsInWishlist ?? []).filter(wishlistSong => wishlistSong.songId !== song.songId));
		} else {
			session.setSongsInWishlist((session.songsInWishlist ?? []).concat(song));
		}
		setIsPreviewOpen(false);
	}, [session.songsInWishlist, songInWishlist, song]);

	function handleToSongDetailsPage(e: React.MouseEvent<HTMLDivElement>) {
		e.stopPropagation();
		navigate(`/song-details/${song.songId}`);
	}


	return (
		<Group
			onMouseEnter={() => setIsPreviewOpen(true)}
			onMouseLeave={() => setIsPreviewOpen(false)}
			style={{
				flex: 1,
				position: "relative",
				overflow: "visible",
				minWidth: 300,
				maxWidth: 300,
				flexWrap: "wrap" as const,
				cursor: "pointer",
				boxShadow: isPreviewOpen ? "0px 0px 15px 1px #252841" : undefined,
				transition: "0.5s"
			}}
		>
			<Card
				withBorder={true}
				radius="md"
				p="md"
				bg="#E8F1F9"
				onClick={handleToSongDetailsPage}
				style={{
					width: "100%",
					display: "flex",
					position: "relative",
					overflow: "visible",
					minHeight: type === "marketplace" ? 480 : 400,
					cursor: "pointer",
					flexDirection: "column" as const
				}}
			>
				<Card.Section>
					<CachedImage
						src={`${appSettings.S3BucketBaseUrl}MarketplaceImages/${song.songId}.png`}
						alt={song.songName}
						height={180}
						width="100%"
						style={{ borderRadius: "var(--mantine-radius-md)", objectFit: "cover", width: "100%" }}
					/>
				</Card.Section>

				<Card.Section px={8} pb={8} pt="md">
					<Group justify="apart">
						<Text fz="lg" fw={500}>
							{song.songName}
						</Text>
					</Group>
					<Text fz="sm" mt="xs">
						{artists}
					</Text>
					{type === "marketplace" && song.price && (
						<Badge size="lg" variant="dark">{"$" + song.price?.toFixed(2)}</Badge>
					)}
				</Card.Section>

				<Card.Section px={8} pb={8}>
					<Text size="xs" mt="md" c="dimmed">
						Tags
					</Text>
					<Group gap={7} mt={18} style={{ flexWrap: "wrap" }}>
						{tagBadgesToRender}
					</Group>
				</Card.Section>

				{type === "marketplace" ? (
					(session.userSongs == null || !songInLibrary) ? (
						<Group
						justify={"center"}
							mt="xs"
							style={{
								height: 30,
								position: "absolute",
								top: "85%",
								left: 0,
								right: 0,
							}}
						>
							{song.price &&
								<Button
									radius="md"
									m={10}
									p={3}
									style={{ flex: 1, maxWidth: "70%" }}
									onClick={handleAddToCart}
								>
									{songInCart ? "Added To Cart" : "Add To Cart"}
								</Button>
							}
							{song.price ? (
								<ActionIcon
									variant="default"
									radius="md"
									size={36}
									style={{ color: "#3A668C" }}
									onClick={handleAddToWishlist}
								>
									{songInWishlist
										? <IconHeartFilled style={{ color: "#3A668C" }} />
										: <IconHeart style={{ color: "#3A668C" }} />
									}
								</ActionIcon>
							) : (
								<ActionIcon
									variant="default"
									radius="md"
									size={36}
									w={"20%"}
									style={{ color: "#3A668C" }}
									onClick={handleAddToWishlist}
								>
									{songInWishlist
										? <IconHeartFilled style={{ color: "#3A668C" }} />
										: <IconHeart style={{ color: "#3A668C" }} />
									}
								</ActionIcon>
							)
							}
						</Group>
					) : (
						<Group
							mt="xs"
							style={{
								height: 30,
								position: "absolute",
								top: "85%",
								left: 0,
								right: 0,
							}}
						>
							<Button
								m={10}
								radius="md"
								style={{ flex: 1, maxWidth: "70%", background: "lightgrey" }}
								disabled
							>
								Added To Library
							</Button>
						</Group>
					)
				) : null}
			</Card>
			<SongPreview songId={song.songId} isOpen={isPreviewOpen} type={type} />
		</Group>
	)
}

export default SongBlock;